import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { AgentCredentialGrantInput } from '../types/agent-credentials';

// Agent credential grants — see types/agent-credentials.ts for semantics.

function useAgentApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).agents, [spaceId]);
}

const grantsKey = (spaceId: string, agentId: string | undefined) =>
  ['space', spaceId, 'agents', agentId, 'credentials'] as const;

export function useAgentCredentialGrants(agentId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: grantsKey(spaceId, agentId),
    queryFn: () => api.listCredentialGrants(agentId!),
    enabled: !!agentId,
  });
}

/** Picker options — only fetched while the editor is open (Member+ endpoint). */
export function useGrantableAgentCredentials(agentId: string | undefined, enabled: boolean) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: [...grantsKey(spaceId, agentId), 'grantable'],
    queryFn: () => api.listGrantableCredentials(agentId!),
    enabled: !!agentId && enabled,
    staleTime: 60 * 1000,
  });
}

export function useSetAgentCredentialGrants(agentId: string) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grants: AgentCredentialGrantInput[]) => api.setCredentialGrants(agentId, grants),
    onSuccess: (grants) => {
      queryClient.setQueryData(grantsKey(spaceId, agentId), grants);
    },
  });
}
